import { defineStore } from "pinia";
import { $api } from "~/composables/api";
import type { Auth, IUser } from "~/types";

export const useUserStore = defineStore("user", {
  state: (): IUser => ({
    playerId: 0,
    name: "",
    alias: "",
    email: "",
    city: "",
    mobile: "",
    avatarUrl: "",
    points: 0,
    todayPoints: 0,
    dateCreated: new Date(),
    dateUpdated: new Date(),
    accountId: 0,
    account: undefined,
    levelId: 0,
    level: undefined,
    languageId: 0,
    language: undefined,
    currentRFIDTagId: undefined,
    currentRFIDTag: undefined,
    pass: undefined,
    currentRank: 0,
    previousLogin: undefined,
    dateOfBirth: undefined,
    gender: "",
    kioskAccessed: false,
  }),
  getters: {
    loggedIn: (state): boolean => {
      return !!state.playerId && !!state.name;
    },
    profileCompleted: (state): boolean => {
      return !!state.alias && !!state.avatarUrl;
    },
  },
  actions: {
    reset() {
      this.setUser({
        playerId: 0,
        name: "",
        alias: "",
        email: "",
        city: "",
        mobile: "",
        avatarUrl: "",
        dateOfBirth: undefined,
        gender: "",
        points: 0,
        todayPoints: 0,
        dateCreated: new Date(),
        dateUpdated: new Date(),
        accountId: 0,
        account: undefined,
        levelId: 0,
        level: undefined,
        languageId: 0,
        language: undefined,
        currentRFIDTagId: undefined,
        currentRFIDTag: undefined,
        pass: undefined,
        currentRank: 0,
        previousLogin: undefined,
      } as IUser);
    },
    setUser(data: IUser) {
      this.playerId = data.playerId;
      this.name = data.name;
      this.alias = data.alias;
      this.email = data.email;
      this.mobile = data.mobile;
      this.city = data.city;
      this.avatarUrl = data.avatarUrl;
      this.dateOfBirth = data.dateOfBirth;
      this.gender = data.gender;
      this.points = data.points;
      this.todayPoints = data.todayPoints;
      this.dateCreated = data.dateCreated;
      this.dateUpdated = data.dateUpdated;
      this.accountId = data.accountId;
      this.account = data.account;
      this.levelId = data.levelId;
      this.level = data.level;
      this.languageId = data.languageId;
      this.language = data.language;
      this.currentRFIDTagId = data.currentRFIDTagId;
      this.currentRFIDTag = data.currentRFIDTag;
      this.pass = data.passes ? data.passes[0] : undefined;
      this.currentRank = data.currentRank;
      this.previousLogin = data.previousLogin;
      this.kioskAccessed = data.kioskAccessed;
    },
    async login(alias: string, password: string) {
      try {
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
        const data = await $api("/auth/player", {
          method: "POST",
          body: {
            alias,
            password,
          },
        }).then((res) => res as Auth);
        this.setUser(data.user);
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value =
          data.token;
      } catch (error) {
        console.error(error);
      }
    },
    async loginWithKey(
      key: string,
      id: number,
      { silent = false }: { silent?: boolean } = {},
    ) {
      try {
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
        const data = await $api("/auth/player/key", {
          method: "POST",
          body: {
            key,
            id,
          },
        }).then((res) => res as Auth);
        this.setUser(data.user);
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value =
          data.token;
      } catch (error) {
        console.error(error);
        if (!silent) throw error;
      }
    },
    async getUserInfo() {
      try {
        const data = await $api("/auth/me").then((res) => res as IUser);
        this.setUser(data);
      } catch (error) {
        console.error(error);
      }
    },
    async buyTicket({ name, mobile }: { name: string; mobile: string }) {
      const data = {
        name,
        mobile,
        password: "PlayMAXX2024",
        parkId: 1,
      };
      await $api("/Player/buyTicket", {
        method: "POST",
        body: data,
      });
    },
    async setupStepOne({
      name,
      city,
      mobile,
    }: {
      name: string;
      city: string;
      mobile: string;
    }) {
      const data = {
        name,
        city,
        mobile,
      };
      await $api("/auth/me", {
        method: "PATCH",
        body: data,
      });
      await this.getUserInfo();
    },
    async setupStepTwo({
      alias,
      avatarUrl,
      kioskAccessed,
    }: {
      alias: string;
      avatarUrl: string;
      kioskAccessed: boolean;
    }) {
      const data = {
        alias,
        avatarUrl,
        kioskAccessed,
      };
      await $api("/auth/me", {
        method: "PATCH",
        body: data,
      });
      // await this.login(alias, 'PlayMAXX2024')
      await this.getUserInfo();
    },
    async updateProfile({
      name,
      city,
      mobile,
      alias,
      avatarUrl,
      gender,
    }: {
      name: string;
      city: string;
      mobile: string;
      alias: string;
      avatarUrl: string;
      gender: string;
    }) {
      const data = {
        name,
        city,
        mobile,
        alias,
        avatarUrl,
        gender,
      };
      await $api("/auth/me", {
        method: "PATCH",
        body: data,
      });

      await this.getUserInfo();
    },
    async updateKioskAccess(kioskAccessed: boolean) {
      const data = {
        kioskAccessed: kioskAccessed,
      };
      await $api("/auth/me", {
        method: "PATCH",
        body: data,
      });
      await this.getUserInfo();
    },
    async logout() {
      await $api("/auth", {
        method: "DELETE",
      });
      this.reset();
      useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
    },
  },
});
